import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { useChangeClientMutation, useLazyGetClientsQuery, useLazyGetConsultationsQuery } from '../services/users.api';
import { ActiveClients } from '../components/journal/active-clients';
import { ArchiveClients } from '../components/journal/archive-clients';
import { CreateClientForm, IFormDataClient } from '../components/create-client-form';
import { TabList, Heading } from '../shared/ui';
import { IClient } from '../store/types';
import { usePosthogCapture } from '../hooks/usePosthogCapture';
import { useBreakpoint } from '../hooks/useBreakpoint';
import { Breakpoint } from '../utils/breakpoint';

import { JournalMobile } from './journal/journal-mobile';

const tabs = [
  { id: 1, title: 'Активные клиенты' },
  { id: 2, title: 'Архивные клиенты' },
  { id: 3, title: 'Групповая терапия' },
];

import { resolveJournalOverviewTab, type JournalLocationState } from './journal/journal-navigation';

export type { JournalLocationState };

const normalizeId = (value: number | string | null | undefined): number | null => {
  if (value === null || value === undefined) {
    return null;
  }
  const numeric = Number(value);
  return Number.isNaN(numeric) ? null : numeric;
};

const Journal = () => {
  const [getClients, { data: clients }] = useLazyGetClientsQuery();
  const [getConsultations, { data: consultations, isFetching: isConsultationsFetching }] =
    useLazyGetConsultationsQuery();
  const [changeClient] = useChangeClientMutation();
  const location = useLocation();
  const navigate = useNavigate();
  const locationState = (location.state as JournalLocationState | null) ?? null;
  const normalizedClientId = normalizeId(locationState?.clientId ?? null);
  const initialActiveTab = resolveJournalOverviewTab(locationState);
  const targetClientIdRef = useRef<number | null>(normalizedClientId);
  const openedFromListRef = useRef(false);
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState(initialActiveTab);
  const [activeClient, setActiveClient] = useState<number | null>(null);
  const pendingSessionDetail = locationState?.openSessionDetail ?? null;
  const [isSessionDetailMode, setIsSessionDetailMode] = useState(false);
  const [isEditClientOpen, setIsEditClientOpen] = useState(false);
  const [editedClient, setEditedClient] = useState<IClient | null>(null);
  const captureEvent = usePosthogCapture();
  const breakpoint = useBreakpoint();
  const isMobile = breakpoint < Breakpoint.Md;

  const fetchClients = useCallback(() => {
    if (activeTab === 2) {
      getClients({ sort: 'DESC', is_active: false, full_name: search });
    } else {
      getClients({ sort: 'DESC', is_active: true, full_name: search });
    }
  }, [activeTab, getClients, search]);

  // Порядок тот же, что в списке слева: иначе «первый» здесь и «первый» на экране — разные клиенты.
  const filteredClients = useMemo(() => {
    const list = clients ?? [];
    const byTab = activeTab === 3 ? list.filter((c) => c.is_group === true) : list.filter((c) => !c.is_group);
    return [...byTab].sort((a, b) => a.full_name.localeCompare(b.full_name, 'ru', { sensitivity: 'base' }));
  }, [clients, activeTab]);

  const handleEditClient = useCallback((client: IClient) => {
    setEditedClient(client);
    setIsEditClientOpen(true);
  }, []);

  const handleCloseEditClient = useCallback(() => {
    setIsEditClientOpen(false);
    setEditedClient(null);
  }, []);

  useEffect(() => {
    if (clients === undefined) return;
    if (filteredClients.length === 0) {
      targetClientIdRef.current = null;
      setActiveClient(null);
      return;
    }

    const targetId = normalizeId(targetClientIdRef.current);
    if (targetId !== null) {
      const match = filteredClients.find((client) => normalizeId(client.id) === targetId);
      if (match) {
        targetClientIdRef.current = null;
        setActiveClient(normalizeId(match.id));
        return;
      }
      // Клиента нет в текущем списке (другая вкладка, поиск, архив) — цель снимается,
      // иначе выбор завис бы на нём и первый клиент никогда бы не подставился.
      targetClientIdRef.current = null;
    }

    const currentActive = normalizeId(activeClient);
    if (currentActive !== null && filteredClients.some((client) => normalizeId(client.id) === currentActive)) {
      return;
    }

    setActiveClient(normalizeId(filteredClients[0]?.id ?? null));
  }, [clients, filteredClients, activeClient]);

  useEffect(() => {
    if (!locationState?.openSessionDetail) {
      return;
    }

    if (locationState.clientId !== undefined) {
      targetClientIdRef.current = normalizeId(locationState.clientId);
    }
    if (locationState.clientId !== undefined || locationState.journalTabId !== undefined) {
      setActiveTab(resolveJournalOverviewTab(locationState));
    }
  }, [locationState]);

  // Открытие сессии — переход в истории браузера, поэтому стрелка «назад» возвращает
  // к списку сессий того же клиента, ровно как кнопка «Назад к сессиям».
  const handleOpenSessionDetail = useCallback(
    (consultationId: number, initialTab: string) => {
      openedFromListRef.current = true;
      navigate(location.pathname, {
        // clientId обязателен: после F5 состояние восстановится из истории, и без него
        // подставился бы первый клиент, у которого этой сессии нет.
        state: {
          ...locationState,
          clientId: activeClient ?? locationState?.clientId,
          journalTabId: activeTab,
          openSessionDetail: { consultationId, initialTab },
        },
      });
    },
    [activeClient, activeTab, location.pathname, locationState, navigate],
  );

  // Шаг назад по истории, если сессию открыли отсюда же. По прямой ссылке
  // (`/journal/:id` редиректит с replace) предыдущей записи нет — там просто закрываем сессию.
  const handleCloseSessionDetail = useCallback(() => {
    if (openedFromListRef.current) {
      openedFromListRef.current = false;
      navigate(-1);
      return;
    }

    navigate(location.pathname, {
      replace: true,
      state: { ...locationState, openSessionDetail: undefined },
    });
  }, [location.pathname, locationState, navigate]);

  useEffect(() => {
    if (activeClient) {
      getConsultations({
        client_entity: `/api/v1/clients/${activeClient}`,
        'sort[created_at]': 'desc',
      });
    }
  }, [activeClient, getConsultations]);

  useEffect(() => {
    if (!activeClient || !pendingSessionDetail) {
      return;
    }

    getConsultations({
      client_entity: `/api/v1/clients/${activeClient}`,
      'sort[created_at]': 'desc',
    });
  }, [activeClient, pendingSessionDetail, getConsultations]);

  // При F5 openSessionDetail приходит из истории сразу, а консультации грузятся асинхронно.
  // Без этого флага пустой список на первом рендере читался бы как «сессия не найдена».
  const isSessionDetailPending = isConsultationsFetching || activeClient === null || consultations === undefined;

  const getTab = () => {
    switch (activeTab) {
      case 1:
        return (
          <ActiveClients
            consultations={activeClient ? consultations || [] : []}
            activeClient={activeClient}
            setActiveClient={setActiveClient}
            search={search}
            setSearch={setSearch}
            clients={filteredClients}
            onEditClient={handleEditClient}
            onSessionDetailModeChange={setIsSessionDetailMode}
            pendingSessionDetail={pendingSessionDetail}
            isSessionDetailPending={isSessionDetailPending}
            onOpenSessionDetail={handleOpenSessionDetail}
            onCloseSessionDetail={handleCloseSessionDetail}
          />
        );
      case 2:
        return (
          <ArchiveClients
            consultations={activeClient ? consultations || [] : []}
            activeClient={activeClient}
            setActiveClient={setActiveClient}
            search={search}
            setSearch={setSearch}
            clients={filteredClients}
            onEditClient={handleEditClient}
            onSessionDetailModeChange={setIsSessionDetailMode}
            pendingSessionDetail={pendingSessionDetail}
            isSessionDetailPending={isSessionDetailPending}
            onOpenSessionDetail={handleOpenSessionDetail}
            onCloseSessionDetail={handleCloseSessionDetail}
          />
        );
      case 3:
        return (
          <ActiveClients
            consultations={activeClient ? consultations || [] : []}
            activeClient={activeClient}
            setActiveClient={setActiveClient}
            search={search}
            setSearch={setSearch}
            clients={filteredClients}
            onEditClient={handleEditClient}
            onSessionDetailModeChange={setIsSessionDetailMode}
            pendingSessionDetail={pendingSessionDetail}
            isSessionDetailPending={isSessionDetailPending}
            onOpenSessionDetail={handleOpenSessionDetail}
            onCloseSessionDetail={handleCloseSessionDetail}
            isGroupTherapy
          />
        );
      default:
        return null;
    }
  };

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  useEffect(() => {
    setIsSessionDetailMode(false);
  }, [activeTab]);

  useEffect(() => {
    if (location.pathname.split('/').length === 2) {
      captureEvent('journal_overview_viewed');
    }
  }, [captureEvent, location.pathname]);

  const handleSaveClient = async (formDataClient: IFormDataClient) => {
    if (!editedClient) {
      return;
    }

    try {
      await changeClient({
        id: editedClient.id,
        full_name: formDataClient.name,
        time_zone: formDataClient.tz,
        is_active: formDataClient.active,
      }).unwrap();
      toast.success(editedClient.is_group ? 'Встреча успешно изменена' : 'Клиент успешно изменён');
      fetchClients();
    } catch (error) {
      toast.error(editedClient.is_group ? 'Не удалось изменить встречу' : 'Не удалось изменить клиента');
    }
  };

  const isOverview = location.pathname.split('/').length === 2;

  return (
    <div className="w-full min-w-0 min-h-full overflow-x-clip bg-surface-muted">
      {isOverview &&
        (isMobile ? (
          <JournalMobile
            tabs={tabs}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            search={search}
            setSearch={setSearch}
            clients={filteredClients}
            consultations={activeClient ? consultations || [] : []}
            activeClient={activeClient}
            setActiveClient={setActiveClient}
            onEditClient={handleEditClient}
            pendingSessionDetail={pendingSessionDetail}
            isSessionDetailPending={isSessionDetailPending}
            onOpenSessionDetail={handleOpenSessionDetail}
            onCloseSessionDetail={handleCloseSessionDetail}
          />
        ) : (
          <div className="w-full min-w-0 px-6 pb-[85px] pt-6">
            {!isSessionDetailMode && (
              <Heading size="xl" className="mb-5">
                Терапевтический журнал
              </Heading>
            )}

            {!isSessionDetailMode && (
              <TabList
                className="mb-5"
                items={tabs.map((item) => ({ id: item.id, label: item.title }))}
                value={activeTab}
                onChange={setActiveTab}
              />
            )}

            {getTab()}
          </div>
        ))}

      <CreateClientForm
        clientData={editedClient ?? undefined}
        open={isEditClientOpen}
        close={handleCloseEditClient}
        save={handleSaveClient}
      />
    </div>
  );
};

export default Journal;
